import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Gauge, Play, Square, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button, Card, cx, Input } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { intlLocale } from "@/lib/i18n";

/**
 * Lasttest-Kunde für Performance-Messungen: viele Testdaten ohne Shopify und ohne FFN.
 * Anlegen/Löschen läuft im Hintergrund; „Messen“ führt typische Suchen gegen den Testkunden aus.
 */
export function LoadTestCard() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["admin-load-test"], queryFn: api.admin.loadTest, refetchInterval: (q) => (q.state.data && !q.state.data.exists ? 20_000 : 10_000) });
  const [sizes, setSizes] = useState({ products: "20000", orders: "100000", inbounds: "3000" });
  const [results, setResults] = useState<{ label: string; ms: number; hits: number | string }[] | null>(null);
  const [measuring, setMeasuring] = useState(false);

  const seed = useMutation({
    mutationFn: () => api.admin.seedLoadTest({ products: Number(sizes.products), orders: Number(sizes.orders), inbounds: Number(sizes.inbounds) }),
    onSuccess: () => {
      toast.success("Testdaten werden erzeugt", { description: "Dauert je nach Menge einige Minuten – der Stand aktualisiert sich hier." });
      void qc.invalidateQueries({ queryKey: ["admin-load-test"] });
    },
    onError: (e) => toast.error("Nicht gestartet", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  const remove = useMutation({
    mutationFn: api.admin.deleteLoadTest,
    onSuccess: () => {
      toast.success("Testdaten werden gelöscht");
      setResults(null);
      void qc.invalidateQueries({ queryKey: ["admin-load-test"] });
      void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
    },
  });

  /** typische Suchen und Listen gegen den Testkunden, Zeit im Browser gemessen (inkl. Netzwerk) */
  const measure = async (tid: string) => {
    setMeasuring(true);
    const runs: { label: string; fn: () => Promise<number | string> }[] = [
      { label: "Aufträge – erste Seite", fn: async () => (await api.orders.search(tid, {})).items.length },
      { label: "Aufträge – Name „Müller“", fn: async () => (await api.orders.search(tid, { q: "Müller" })).items.length },
      { label: "Aufträge – Nummer „#LT0050000“", fn: async () => (await api.orders.search(tid, { q: "#LT0050000" })).items.length },
      { label: "Aufträge – Tracking-Nr.", fn: async () => (await api.orders.search(tid, { q: "003404340000000077" })).items.length },
      { label: "Aufträge – E-Mail", fn: async () => (await api.orders.search(tid, { q: "kunde4711@example.com" })).items.length },
      { label: "Aufträge – SKU „LT-001234“", fn: async () => (await api.orders.search(tid, { q: "LT-001234" })).items.length },
      { label: "Aufträge – Reiter „Zurückgehalten“", fn: async () => (await api.orders.search(tid, { status: "on_hold,failed" })).items.length },
      { label: "Artikel – erste Seite", fn: async () => (await api.products.list(tid, { pageSize: 50 } as never)).items.length },
      { label: "Artikel – Suche „Serum“", fn: async () => (await api.products.list(tid, { search: "Serum", pageSize: 50 } as never)).items.length },
      { label: "Bestände – erste Seite", fn: async () => (await api.stock.page(tid, { pageSize: 100 })).total },
      { label: "Bestände – Suche „Lavendel“", fn: async () => (await api.stock.page(tid, { search: "Lavendel", pageSize: 100 })).total },
      { label: "Bestände – Filter „Knapp“", fn: async () => (await api.stock.page(tid, { filter: "low", pageSize: 100 })).total },
      { label: "Wareneingänge – Liste", fn: async () => (await api.inbounds.list(tid)).length },
      { label: "Wareneingänge – Suche „L2450“ (Charge)", fn: async () => (await api.inbounds.list(tid, { q: "L2450" })).length },
    ];
    const out: { label: string; ms: number; hits: number | string }[] = [];
    for (const r of runs) {
      const t0 = performance.now();
      try {
        const hits = await r.fn();
        out.push({ label: r.label, ms: Math.round(performance.now() - t0), hits });
      } catch (e) {
        out.push({ label: r.label, ms: Math.round(performance.now() - t0), hits: e instanceof ApiError ? `Fehler ${e.status}` : "Fehler" });
      }
      setResults([...out]);
    }
    setMeasuring(false);
  };

  const s = status.data;
  const tone = (ms: number) => (ms < 400 ? "text-success" : ms < 1200 ? "text-warning" : "text-danger");
  return (
    <Card className="p-5 mt-4">
      <h2 className="font-semibold text-[14px] flex items-center gap-2"><FlaskConical className="size-4 text-fg-muted" /> Lasttest</h2>
      <p className="text-[12.5px] text-fg-muted mt-0.5">
        Eigener Testkunde „Lasttest (Testdaten)“ mit vielen Datensätzen – ohne Shopify-Shop und ohne FFN-Verbindung. Aufträge sind als Historie markiert und werden nie verarbeitet oder übertragen.
      </p>

      {!s ? null : !s.exists ? (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          {(["products", "orders", "inbounds"] as const).map((k) => (
            <label key={k} className="block">
              <span className="text-[12px] font-medium text-fg-muted">{k === "products" ? "Artikel" : k === "orders" ? "Aufträge" : "Wareneingänge"}</span>
              <Input value={sizes[k]} onChange={(e) => setSizes((x) => ({ ...x, [k]: e.target.value.replace(/[^0-9]/g, "") }))} inputMode="numeric" className="mt-1 w-32 [&_input]:text-right" />
            </label>
          ))}
          <Button variant="primary" loading={seed.isPending} onClick={() => seed.mutate()}>Testdaten erzeugen</Button>
        </div>
      ) : (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px]">
            <span><b className="tabular-nums">{fmtNumber(s.products ?? 0)}</b> Artikel</span>
            <span><b className="tabular-nums">{fmtNumber(s.orders ?? 0)}</b> Aufträge</span>
            <span><b className="tabular-nums">{fmtNumber(s.orderLines ?? 0)}</b> Positionen</span>
            <span><b className="tabular-nums">{fmtNumber(s.inbounds ?? 0)}</b> Wareneingänge</span>
            <span className="text-fg-subtle">angelegt {fmtRelative(s.createdAt!)}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button icon={<Gauge className="size-4" />} loading={measuring} onClick={() => void measure(s.tenantId!)}>Suchen messen</Button>
            <Button variant="ghost" icon={<Trash2 className="size-4" />} loading={remove.isPending} onClick={() => { if (confirm("Lasttest-Kunde mit allen Testdaten löschen?")) remove.mutate(); }}>Testdaten löschen</Button>
          </div>
          {results && (
            <table className="mt-3 w-full text-[13px]">
              <thead className="text-[12px] text-fg-subtle"><tr><th className="text-left font-medium py-1">Abfrage</th><th className="text-right font-medium">Treffer</th><th className="text-right font-medium">Zeit</th></tr></thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.label} className="border-t border-border">
                    <td className="py-1.5">{r.label}</td>
                    <td className="text-right tabular-nums text-fg-muted">{typeof r.hits === "number" ? fmtNumber(r.hits) : r.hits}</td>
                    <td className={cx("text-right tabular-nums font-medium", tone(r.ms))}>{fmtNumber(r.ms)} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="mt-2 text-[12px] text-fg-subtle">Gemessen im Browser inkl. Netzwerk. Über die Kundenliste lässt sich auch direkt in den Testkunden wechseln.</p>
          <LoadRunPanel />
        </div>
      )}
    </Card>
  );
}

/** Sekunden lesbar: 42 s, 3,5 min, 1,2 h */
const fmtSec = (v: number | null | undefined) => (v == null ? "–" : v < 90 ? `${fmtNumber(Math.round(v))} s` : v < 5400 ? `${(v / 60).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} min` : `${(v / 3600).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} h`);

/**
 * Lastlauf (nur Testumgebung): neue Aufträge laufen über die Zeit verteilt durch die echte Verarbeitung –
 * Prüfung, Regeln, Versandwahl, Übergabe ans simulierte Lager und Versandmeldung zurück.
 */
function LoadRunPanel() {
  const qc = useQueryClient();
  const env = useQuery({ queryKey: ["app-env"], queryFn: api.env, staleTime: Infinity, retry: false });
  const staging = env.data?.env === "staging";
  const report = useQuery({ queryKey: ["admin-load-run"], queryFn: api.admin.loadRun, enabled: staging, refetchInterval: (q) => (q.state.data?.run && !q.state.data.run.finishedAt ? 5_000 : 30_000) });
  const [form, setForm] = useState({ orders: "20000", minutes: "120", latencyMs: "250", shipMinutes: "10" });
  const start = useMutation({
    mutationFn: () => api.admin.startLoadRun({ orders: Number(form.orders), minutes: Number(form.minutes), latencyMs: Number(form.latencyMs), shipMinutes: Number(form.shipMinutes) }),
    onSuccess: () => {
      toast.success("Lastlauf gestartet", { description: "Das simulierte Lager wird aktiviert, die ersten Aufträge kommen in wenigen Sekunden." });
      setTimeout(() => void qc.invalidateQueries({ queryKey: ["admin-load-run"] }), 2000);
    },
    onError: (e) => toast.error("Nicht gestartet", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  const stop = useMutation({
    mutationFn: api.admin.stopLoadRun,
    onSuccess: () => {
      toast.success("Lastlauf wird beendet", { description: "Bereits angelegte Aufträge laufen zu Ende." });
      setTimeout(() => void qc.invalidateQueries({ queryKey: ["admin-load-run"] }), 2000);
    },
  });
  if (!staging) return null;

  const d = report.data;
  const run = d?.run;
  const running = !!run && !run.finishedAt;
  const st = d?.stats;
  const fields = [
    { k: "orders", label: "Aufträge" },
    { k: "minutes", label: "Dauer (min)" },
    { k: "latencyMs", label: "Lager-Antwortzeit (ms)" },
    { k: "shipMinutes", label: "Versand nach (min)" },
  ] as const;
  const perHour = Number(form.minutes) > 0 ? Math.round((Number(form.orders) / Number(form.minutes)) * 60) : 0;
  return (
    <div className="mt-5 border-t border-border pt-4">
      <h3 className="font-semibold text-[13.5px] flex items-center gap-2"><Play className="size-4 text-fg-muted" /> Lastlauf mit simuliertem Lager</h3>
      <p className="text-[12.5px] text-fg-muted mt-0.5">
        Neue Aufträge laufen gleichmäßig verteilt durch die echte Verarbeitung bis zur Übergabe ans simulierte Lager, das nach der eingestellten Zeit Versand mit Tracking meldet. Nur in der Testumgebung – kein FFN, kein Shop, keine Mails an Endkunden.
      </p>
      {!running && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          {fields.map((f) => (
            <label key={f.k} className="block">
              <span className="text-[12px] font-medium text-fg-muted">{f.label}</span>
              <Input value={form[f.k]} onChange={(e) => setForm((x) => ({ ...x, [f.k]: e.target.value.replace(/[^0-9]/g, "") }))} inputMode="numeric" className="mt-1 w-32 [&_input]:text-right" />
            </label>
          ))}
          <Button variant="primary" icon={<Play className="size-4" />} loading={start.isPending} onClick={() => start.mutate()}>Lastlauf starten</Button>
          <span className="text-[12px] text-fg-subtle pb-2">≈ {fmtNumber(perHour)} Aufträge/Stunde</span>
        </div>
      )}
      {run && st && (
        <div className="mt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[12.5px] text-fg-muted">
              {running ? "Läuft" : run.error ? `Beendet (${run.error})` : "Abgeschlossen"} · gestartet {fmtRelative(run.startedAt)} · {fmtNumber(run.created)} von {fmtNumber(run.orders)} Aufträgen angelegt ({fmtNumber(run.minutes)} min)
            </span>
            {running && <Button variant="ghost" size="sm" icon={<Square className="size-3.5" />} loading={stop.isPending} onClick={() => stop.mutate()}>Abbrechen</Button>}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-surface-sunken overflow-hidden">
            <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, (st.handed_over / Math.max(1, run.orders)) * 100)}%` }} />
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
            {[
              { l: "Übergeben ans Lager", v: fmtNumber(st.handed_over) },
              { l: "Versendet", v: fmtNumber(st.shipped) },
              { l: "Warten auf Verarbeitung", v: fmtNumber(st.waiting) },
              { l: "Zurückgehalten / Fehler", v: `${fmtNumber(st.on_hold)} / ${fmtNumber(st.failed)}`, bad: st.on_hold + st.failed > 0 },
              { l: "Durchsatz (letzte 5 min)", v: `${fmtNumber(st.perMinute)} / min` },
              { l: "Eingang → Übergabe (Median)", v: fmtSec(st.p50) },
              { l: "Eingang → Übergabe (95 %)", v: fmtSec(st.p95), bad: (st.p95 ?? 0) > 900 },
              { l: "Eingang → Übergabe (max.)", v: fmtSec(st.pmax) },
            ].map((x) => (
              <div key={x.l} className="rounded-lg border border-border px-3 py-2">
                <div className="text-[11.5px] text-fg-subtle">{x.l}</div>
                <div className={cx("font-semibold tabular-nums", x.bad && "text-danger")}>{x.v}</div>
              </div>
            ))}
          </div>
          {!!d?.holds?.length && (
            <p className="mt-2 text-[12.5px] text-fg-muted">Häufigste Gründe: {d.holds.map((h) => `${h.reason} (${fmtNumber(h.n)})`).join(" · ")}</p>
          )}
          {!!d?.queues?.length && (
            <p className="mt-1 text-[12px] text-fg-subtle">
              Warteschlangen: {d.queues.map((q) => `${q.key === "realtime" ? "Echtzeit" : "Hintergrund"} ${fmtNumber((q.waiting ?? 0) + (q.prioritized ?? 0))} wartend, ${fmtNumber(q.active ?? 0)} aktiv, ${fmtNumber(q.delayed ?? 0)} geplant`).join(" · ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
